import { useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Battery } from "@/types/battery";
import { Case, CaseFilter, CasePaginatedResult, CaseResult, CategoryListResponse, ComponentListResponse } from "@/types/cases";
import { Device, DeviceFilter, DevicePaginatedResult } from "@/types/device";
import { EventConfirmResult, EventFilter, EventInfoResult, EventPaginatedResult, EventTypes } from "@/types/event";
import { Inverter } from "@/types/inverter";
import { Operation, OperationFilter, OperationPaginatedResult, OperationResult } from "@/types/operation";
import { Overview } from "@/types/overview";
import { MonthlyReportFilter } from "@/types/reports";
import { User, UserResetPasswordResult, UserUpdateResult } from "@/types/user";
import { Wallbox } from "@/types/wallbox";
import { dashboardConfig } from "@/config/dashboard";

import { Health } from "../types/health";
import { SortByString, SortOrder } from "../types/sortingFilter";
import { casePayload, confirmEventPayload, ignoreEventPayload, operationPayload, UpdateDisplayNamePayload } from "./payloads";
import {
    addCompany,
    addContact,
    addDeviceGroupManagement,
    addDeviceManagement,
    addNewCase,
    addNewOperation,
    addUserManagementLocation,
    closedCase,
    confirmEvent,
    createTestMail,
    createUser,
    deleteDeviceGroupManagementDetails,
    deleteDeviceManagementDetails,
    deleteUserManagementLocationDetails,
    fetchMonthReport,
    getAuxData,
    getAuxSummary,
    getBattery,
    getCase,
    getCases,
    getCategories,
    getComponents,
    getContact,
    getContacts,
    getContactsSettings,
    getControlData,
    getDevice,
    getDeviceGroupManagementDetails,
    getDeviceGroupsManagement,
    getDeviceManagementDetails,
    getDeviceOverview,
    getDevicesManagement,
    getEventInfos,
    getEvents,
    getExternalConnections,
    getFeatures,
    getFeaturesByRole,
    getHealth,
    getInverter,
    getManagementCompanies,
    getManagementDeviceTypes,
    getManagementMandanten,
    getOperation,
    getOperations,
    getOverview,
    getPrimarydataGeneral,
    getRoles,
    getSearchManagementMandant,
    getSingleLineDiagram,
    getUser,
    getUserManagement,
    getUserManagementDetails,
    getUserManagementLocationDetails,
    getUserManagementLocations,
    getWallbox,
    ignoreEvent,
    resetUserPassword,
    updateCase,
    updateCompany,
    updateContact,
    updateDeviceGroupManagementDetails,
    updateDeviceManagementDetails,
    updateOperation,
    updatePrimaryDataGeneralDisplayName,
    updateUser,
    updateUserManagementLocationDetails,
    updateUserSettings,
    uploadUserAvatar,
} from "./requests";
import { PrimaryDataGeneralUpdate } from "../types/primaryDataGeneral";
import { CreateOrUpdateUserDto, FeaturesItem, MandantSearchDto, Role, UserDetailsResponse, UsersFilter } from "../types/userManagement";
import { MandantManagementFilter } from "../types/mandanten";
import { CreateLocationDto, CreateLocationResponse, LocationDetailsDto, UserLocationFilter } from "../types/UserManagementLocation";
import { CreateDeviceDto, CreateDeviceResponse, DeviceCompanyResponse, DeviceManagementFilter, DeviceTypeManagementResponse } from "../types/deviceManagement";
import { CreateDeviceGroupResponse, CreateDevicesGroupDto, DeviceGroup, DeviceGroupManagementFilter, UpdateDevicesGroupDto } from "../types/deviceGroupManagement";
import { AddResultDTO, ContactPersonDTO, ContactSaveDTO } from "../types/primaryDataContacts";

export function useHealth() {
    return useQuery({
        queryKey: ["Health"],
        queryFn: async ({ signal }): Promise<Health[]> => {
            return await getHealth(signal);
        },
        meta: {
            persist: false,
        },
    });
}

export function useResetUserPassword() {
    return useMutation({
        mutationKey: ["UserResetPassword"],
        mutationFn: async (user: User): Promise<UserResetPasswordResult> => {
            return await resetUserPassword(user)
                .then((x) => x)
                .catch(() => {
                    return {
                        result: false,
                    };
                });
        },
    });
}

export function useUser(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ["User"],
        queryFn: async ({ signal }): Promise<User> => {
            return await getUser(signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        avatar: "",
                        email: "",
                        id: "",
                        personId: "",
                        features: [],
                        deviceFeatures: [],
                        level: "",
                        name: "",
                        username: "",
                        changeEMailUrl: "",
                        changePasswordUrl: "",
                        settings: {
                            notifyOnAlarm: false,
                            theme: "light",
                        },
                    };
                });
        },
        refetchInterval: 3_600_000, // einmal in der stunde
        meta: {
            persist: true,
        },
        enabled: options?.enabled ?? true,
    });
}

export function useUserUpdate() {
    return useMutation({
        mutationKey: ["UserUpdate"],
        mutationFn: async (user: User): Promise<UserUpdateResult> => {
            return await updateUserSettings(user)
                .then((x) => x)
                .catch(() => {
                    return {
                        result: false,
                    };
                });
        },
    });
}

export function useUploadUserAvatar() {
    return useMutation({
        mutationKey: ["UploadUserAvatar"],
        mutationFn: async ({ user, image64 }: { user: User, image64: string }): Promise<boolean> => {
            return await uploadUserAvatar(user, image64)
                .then((x) => x)
                .catch(() => {
                    return false;
                });
        },
    });
}
export function useCreateTestMail() {
    return useMutation({
        mutationKey: ["UserTestMail"],
        mutationFn: async (user: User): Promise<boolean> => {
            return await createTestMail(user)
                .then((x) => x)
                .catch(() => false);
        },
    });
}
export function useUserManagement(filter: UsersFilter) {
    return useQuery({
        queryKey:
            ["user-management",
                filter.page,
                filter.pageSize,
                filter.searchText,
                filter.mandantUuids,
                filter.sortBy,
                filter.sortOrder,
                filter.fromDate,
                filter.userUuids,
                filter.toDate,
                filter.sortOrderNumber,
                filter.isBlocked

            ],
        queryFn: ({ signal }) => getUserManagement(filter, signal),
        placeholderData: (previousData) => previousData,
    });
}
export function useUserManagementDetails(uuid?: string) {
    return useQuery({
        queryKey: ["getUser", uuid],
        queryFn: async ({ signal }): Promise<UserDetailsResponse[]> => {
            return await getUserManagementDetails(uuid as string, signal);
        },
        /*  enabled: Boolean(uuid),*/
    });
}
export function useRoles() {
    return useQuery({
        queryKey: ["roles"],
        queryFn: async ({ signal }): Promise<Role[]> => {
            return await getRoles(signal);
        },
    });
}

export function useFeatures() {
    return useQuery({
        queryKey: ["features"],
        queryFn: async ({ signal }): Promise<FeaturesItem[]> => {
            return await getFeatures(signal);
        },
    });

}
export function useFeaturesByRole(roleId: number) {
    return useQuery({
        queryKey: ["features-by-role", roleId],
        queryFn: async ({ signal }): Promise<FeaturesItem[]> => {
            return await getFeaturesByRole(roleId, signal);
        },
        /*    enabled: roleId >= 0,*/
    });
}

export const useCreateUser = () => {
    return useMutation({
        mutationFn: createUser,
    });
};

export function useUpdateUser() {
    return useMutation({
        mutationFn: ({
            userId,
            data,
        }: {
            userId: string;
            data: CreateOrUpdateUserDto;
        }) => updateUser(userId, data),
    });
}

export function useManagementMandanten(filter: MandantManagementFilter) {
    return useQuery({
        queryKey: ["management-mandanten", filter],
        queryFn: ({ signal }) => getManagementMandanten(filter, signal),
        placeholderData: (previousData) => previousData,
    });
}
export function useSearchMandanten(searchTerm: string, searchByUuid: boolean = false) {

    const searchTermRef = useRef<string>(searchTerm);

    const query = useQuery({
        queryKey: ["mandant-search", searchTerm, searchByUuid],
        queryFn: async ({ signal }): Promise<MandantSearchDto[]> => {
            return await getSearchManagementMandant(searchTermRef.current, searchByUuid, 10, signal)
                .then((x) => x)
                .catch(() => {
                    return [];
                });
        },
    });

    const refetchWithSearch = useCallback(
        (st: string) => {
            searchTermRef.current = st;
            query.refetch();
        },
        [query]
    );
    return { ...query, refetchWithSearch };
}

export function useUserManagementLocations(filter: UserLocationFilter) {

    const query = useQuery({
        queryKey: ["user-management-locations",
            filter.page,
            filter.pageSize,
            filter.searchText,
            filter.locationUuids,
            filter.sortBy,
            filter.sortOrder],
        queryFn: ({ signal }) => getUserManagementLocations(filter, signal),
        placeholderData: (previousData) => previousData,
    });

    return { ...query };
}
export function useAddUserManagementLocation() {
    return useMutation<CreateLocationResponse, Error, CreateLocationDto>({
        mutationFn: async (payload) => {
            return await addUserManagementLocation(payload);
        },
    });
}

export function useUserManagementLocationDetails(locationUuid?: string) {
    return useQuery({
        queryKey: ["getLocation", locationUuid],
        queryFn: async ({ signal }): Promise<LocationDetailsDto> => {
            return await getUserManagementLocationDetails(locationUuid as string, signal);
        },
        enabled: Boolean(locationUuid),
    });
}

export function useUpdateUserManagementLocation() {
    return useMutation<boolean, Error, { locationUuid: string; data: LocationDetailsDto }>({
        mutationFn: async ({ locationUuid, data }) => {
            return await updateUserManagementLocationDetails(locationUuid, data);
        },
    });
}
export function useDeleteUserManagementLocation() {
    return useMutation<boolean, Error, string>({
        mutationFn: async (locationUuid) => {
            return await deleteUserManagementLocationDetails(locationUuid);
        },
    });
}
export function useDevicesManagement(filter: DeviceManagementFilter) {
    return useQuery({
        queryKey: ["devices-management",
            filter.page,
            filter.pageSize,
            filter.searchText,
            filter.deviceUuids,
            filter.manufactureUuids,
            filter.primaryCompanyUuids,
            filter.locationUuids,
            filter.typeDesignIds,
            filter.datasourceIds,
            filter.isDelete,
            filter.isArchived,
            filter.sortBy,
            filter.sortOrder],
        queryFn: ({ signal }) => getDevicesManagement(filter, signal),
        placeholderData: (previousData) => previousData,
    });
}

export function useDevicesManagementDetails(deviceUuid?: string) {
    return useQuery({
        queryKey: ["getDevice", deviceUuid],
        queryFn: async ({ signal }): Promise<CreateDeviceDto> => {
            return await getDeviceManagementDetails(deviceUuid as string, signal);
        },
        enabled: Boolean(deviceUuid),
    });
}
export function useAddDeviceManagement() {
    return useMutation<CreateDeviceResponse, Error, CreateDeviceDto>({
        mutationFn: async (payload) => {
            return await addDeviceManagement(payload);
        },
    });
}

export function useUpdateDeviceManagement() {
    return useMutation<boolean, Error, { deviceUuid: string; data: CreateDeviceDto }>({
        mutationFn: async ({ deviceUuid, data }) => {
            return await updateDeviceManagementDetails(deviceUuid, data);
        },
    });
}
export function useDeleteDeviceManagement() {
    return useMutation<boolean, Error, string>({
        mutationFn: async (deviceUuid) => {
            return await deleteDeviceManagementDetails(deviceUuid);
        },
    });
}
export function useManagementCompanies(searchText: string) {
    return useQuery<DeviceCompanyResponse>({
        queryKey: ["managementCompanies", searchText],
        queryFn: ({ signal }) =>
            getManagementCompanies(
                {
                    searchText,
                    page: 0,
                    pageSize: 20,
                    deviceUuids: null,
                    manufactureUuids: null,
                    primaryCompanyUuids: null,
                    locationUuids: null,
                    typeDesignIds: null,
                    datasourceIds: null,
                    isDelete: null,
                    isArchived: null,
                    fromDate: null,
                    toDate: null,
                    sortBy: SortByString.Name,
                    sortOrder: SortOrder.Descending,
                    sortOrderNumber: 0,
                    mandantId: null
                },
                signal),


    });
}

export function useManagementDeviceTypes(searchText: string) {
    return useQuery({
        queryKey: ["managementDeviceTypes", searchText],
        queryFn: async ({ signal }): Promise<DeviceTypeManagementResponse> => {
            return await getManagementDeviceTypes({
                deviceUuids: null,
                userUuids: null,
                manufactureUuids: null,
                primaryCompanyUuids: null,
                locationUuids: null,
                typeDesignIds: null,
                datasourceIds: null,
                isDelete: null,
                isArchived: null,
                searchText,
                fromDate: null,
                toDate: null,
                sortBy: SortByString.Name,
                sortOrder: SortOrder.Ascending,
                sortOrderNumber: 0,
                page: 0,
                pageSize: 20,
                mandantId: null
            }, signal);
        },
    });
}
export function useDeviceGroupsManagement(filter: DeviceGroupManagementFilter) {
    return useQuery({
        queryKey: ["deviceGroup-management",
            filter.page,
            filter.pageSize,
            filter.searchText ?? "",
            filter.sortBy ?? "",
            filter.sortOrder,
            filter.sortOrderNumber,
            filter.isDelete ?? null,
            filter.typeIds?.join(",") ?? "",
            filter.mandantUuids?.join(",") ?? "",
            filter.locationUuids?.join(",") ?? "",
            filter.deviceGroupUuids?.join(",") ?? "",
            filter.deviceUuids?.join(",") ?? "",
            filter.userUuids?.join(",") ?? "",
        ],
        queryFn: ({ signal }) => getDeviceGroupsManagement(filter, signal),
        placeholderData: (previousData) => previousData,
    });
}
export function useAddDeviceGroupManagement() {
    return useMutation<CreateDeviceGroupResponse, Error, CreateDevicesGroupDto>({
        mutationFn: async (payload) => {
            return await addDeviceGroupManagement(payload);
        },
    });
}
export function useDeviceGroupManagementDetails(groupUuid?: string) {
    return useQuery({
        queryKey: ["getGroup", groupUuid],
        queryFn: async ({ signal }): Promise<DeviceGroup> => {
            return await getDeviceGroupManagementDetails(groupUuid as string, signal);
        },
        enabled: Boolean(groupUuid),
    });
}

export function useUpdateDeviceGroupManagementDetails() {
    const queryClient = useQueryClient();
    return useMutation<boolean, Error, { groupUuid: string; data: UpdateDevicesGroupDto }>({
        mutationFn: async ({ groupUuid, data }) => {
            return await updateDeviceGroupManagementDetails(groupUuid, data);
        },


        onSuccess: async (_response, variables) => {
            await queryClient.invalidateQueries({
                queryKey: ["getGroup", variables.groupUuid],
            });

            await queryClient.invalidateQueries({
                queryKey: ["deviceGroup-management"],
            });
        },
    });
}
export function useDeleteDeviceGroupManagementDetails() {
    return useMutation<boolean, Error, string>({
        mutationFn: async (groupUuid) => {
            return await deleteDeviceGroupManagementDetails(groupUuid);
        },
    });
}


export function useOverview(partialLoad: boolean = false) {
    return useQuery({
        queryKey: ["Overview", partialLoad],
        queryFn: async (): Promise<Overview> => {
            const data = await getOverview(partialLoad);

            if (partialLoad) {
                writeOverviewSnapshotThrottled(partialLoad, data);
            }

            return data;
        },
        initialData: () => partialLoad ? readOverviewSnapshot(partialLoad) : undefined,
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        meta: {
            persist: false,
        },
    });
}

export function useDeviceOverview(page: number, pageSize: number, filter: DeviceFilter, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: [
            "DeviceOverview",
            page,
            pageSize,
            filter.name ?? "",
            filter.location ?? "",
            filter.isOnline,
            filter.sortBy,
            filter.sortOrder,
            filter.deviceUuids?.join(",") ?? "",
            filter.conditions?.join(",") ?? "",
        ],
        queryFn: async ({ signal }): Promise<DevicePaginatedResult> => {
            return await getDeviceOverview(page, pageSize, filter, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        currentFilter: {
                            conditions: [],
                            deviceUuids: [],
                            isOnline: true,
                            location: "",
                            name: "",
                            sortBy: SortByString.Name,
                            sortOrder: SortOrder.Ascending,
                        },
                        onlineDevices: 0,
                        offlineDevices: 0,
                        totalDevices: 0,
                        items: [],
                        page: 0,
                        pageId: "",
                        pageSize: 0,
                        totalItems: 0,
                        totalPages: 0,
                        totalItemsPerPage: 0,
                    };
                });
        },
        enabled: options?.enabled ?? true,
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: true,
        },
    });
}

export function useDevice(deviceId: string, deviceType: string, deviceDataHours: number) {
    return useQuery({
        // Important: every value used by queryFn must be part of the queryKey.
        // Otherwise TanStack Query can reuse the same cache entry for different devices.
        queryKey: ["Device", deviceId, deviceType, deviceDataHours],
        queryFn: async ({ signal }): Promise<Device> => {
            return await getDevice(deviceId, deviceType, deviceDataHours, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        id: "",
                        datasourceId: deviceId,
                        online: false,
                        battery: {},
                        status: "offline",
                        dataFlow: [],
                        gridActivePowerTotal: 0,
                        gridEnergyConsumptionToGrid: 0,
                        inverterErrorcode: 0,
                        inverterState: 0,
                        condition: undefined,
                        lastOnline: new Date(),
                        ratedValues: {},
                        name: deviceId,
                        type: deviceType,
                        location: "",
                    };
                });
        },
        enabled: Boolean(deviceId && deviceType),
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: false,
        },
    });
}

export function usePrimaryDataGeneral(deviceUuid: string) {
    return useQuery({
        queryKey: ["general", deviceUuid],
        queryFn: ({ signal }) => getPrimarydataGeneral(deviceUuid, signal),
        enabled: Boolean(deviceUuid),
        refetchOnWindowFocus: false,
        gcTime: 5 * 60_000,
    });
}

export function useUpdateDisplayName() {
    const queryClient = useQueryClient();

    return useMutation<PrimaryDataGeneralUpdate, unknown, UpdateDisplayNamePayload>({
        mutationKey: ["general", "updateDisplayName"],
        mutationFn: ({ deviceUuid, displayName }) =>
            updatePrimaryDataGeneralDisplayName(deviceUuid, displayName),

        onSuccess: (res, vars) => {
            queryClient.setQueryData(["general", vars.deviceUuid], (old: any) => {
                if (!old) return old;
                return { ...old, displayName: res.displayName };
            });
        },
    });
}
export function usePrimaryDataContacts(deviceUuid: string) {
    return useQuery({
        queryKey: ["contacts", deviceUuid],

        queryFn: ({ signal }) =>
            getContacts(deviceUuid, signal),

        enabled: Boolean(deviceUuid),

        refetchOnWindowFocus: false,

        gcTime: 5 * 60_000,
    });
}

export function usePrimaryDataContact(contactUuid: string) {
    return useQuery({
        queryKey: ["contact", contactUuid],
        queryFn: ({ signal }) =>
            getContact(contactUuid, signal),
        enabled: Boolean(contactUuid),
        refetchOnWindowFocus: false,

        gcTime: 5 * 60_000,
    });
}

export function useAddContact(deviceUuid: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (
            contact: ContactSaveDTO
        ): Promise<AddResultDTO> =>
            addContact(contact),

        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["contacts", deviceUuid],
            });
        },
    });
}
type UpdateContactVariables = {
    contactUuid: string;
    contact: ContactPersonDTO;
};

export function useUpdateContact() {
    return useMutation<ContactPersonDTO, Error, UpdateContactVariables>({
        mutationFn: ({ contactUuid, contact }) =>
            updateContact(contactUuid, contact),

    });
}
export function useContactsSettings() {
    return useQuery({
        queryKey: ["contacts", "settings"],
        queryFn: ({ signal }) => getContactsSettings(signal),
        refetchOnWindowFocus: false,
        staleTime: 5 * 60_000,
    });
}
export function useAddCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: addCompany,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["contacts", "settings"],
            });
        },
    });
}
export function useUpdateCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateCompany,
        onSuccess: async (success) => {
            if (success) {
                await queryClient.invalidateQueries({
                    queryKey: ["contacts", "settings"],
                });
            }
        },
    });
}
export function useBattery(deviceId: string, deviceType: string, deviceDataHours: number) {
    return useQuery({
        queryKey: ["DeviceBattery", deviceId, deviceType, deviceDataHours],
        queryFn: async ({ signal }): Promise<Battery> => {
            return await getBattery(deviceId, deviceType, deviceDataHours, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        id: "",
                        datasourceId: deviceId,
                        cellMaxTemperature: 0,
                        cellMinTemperature: 0,
                        cellMaxVoltage: 0,
                        cellMinVoltage: 0,
                        current: 0,
                        type: deviceType,
                        modules: { online: 0, total: 0, },
                        power: 0,
                        soc: 0,
                        soh: 0,
                        statuscodes: { warning: 0, alarm: 0 },
                        temperature: 0,
                        voltage: 0,
                        dataFlow: [],
                    };
                });
        },
        enabled: Boolean(deviceId && deviceType),
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: false,
        },
    });
}
export function useWallbox(deviceId: string, deviceType: string) {
    return useQuery({
        queryKey: ["DeviceWallbox", deviceId, deviceType],
        queryFn: async ({ signal }): Promise<Wallbox> => {
            return await getWallbox(deviceId, deviceType, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        id: "",
                        datasourceId: deviceId,
                        power: 0,
                        statuscode: 0,
                        statuscodeText: "",
                        vehicleStatuscode: 0,
                        vehicleStatuscodeText: "",
                        ratedValues: {
                            power: 0,
                        },
                    };
                });
        },
        enabled: Boolean(deviceId && deviceType),
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: false,
        },
    });
}
export function useInverter(deviceId: string, deviceType: string) {
    return useQuery({
        queryKey: ["DeviceInverter", deviceId, deviceType],
        queryFn: async ({ signal }): Promise<Inverter> => {
            return await getInverter(deviceId, deviceType, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        active: 0,
                        id: "",
                        datasourceId: deviceId,
                        ratedValues: {
                            installed: 0,
                            power: 0,
                        },
                        units: [],
                    };
                });
        },
        enabled: Boolean(deviceId && deviceType),
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: false,
        },
    });
}
export function useExternalConnections(deviceType: string, deviceId: string) {
    return useQuery({
        queryKey: ["external-connections", deviceType, deviceId],
        queryFn: ({ signal }) => getExternalConnections(deviceType, deviceId, signal),
        enabled: Boolean(deviceType && deviceId),
        refetchOnWindowFocus: false,
        gcTime: 5 * 60_000,
    });
}

export function useAux(deviceType: string, deviceId: string) {
    return useQuery({
        queryKey: ["aux", deviceType, deviceId],
        queryFn: ({ signal }) => getAuxData(deviceType, deviceId, signal),
        enabled: Boolean(deviceType && deviceId),
        refetchOnWindowFocus: false,
        gcTime: 5 * 60_000,
    });
}

export const useAuxSummary = (
    deviceType?: string,
    deviceUuid?: string,
    enabled = true
) => {
    return useQuery({
        queryKey: ["aux-summary", deviceType, deviceUuid],
        queryFn: ({ signal }) => getAuxSummary(deviceType!, deviceUuid!, signal),
        enabled: enabled && Boolean(deviceType && deviceUuid),
        retry: false,
    });
};
export function useSingleLineDiagram(deviceType?: string, deviceUuid?: string) {
    return useQuery({
        queryKey: ["single-line-diagram", deviceType, deviceUuid],
        queryFn: ({ signal }) => getSingleLineDiagram(deviceType!, deviceUuid!, signal),
        enabled: Boolean(deviceType && deviceUuid),
        refetchOnWindowFocus: false,
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        gcTime: 60_000,
        staleTime: 0,
    });
}
export function useControlData(deviceType: string, deviceId: string, deviceDataHours: number) {
    return useQuery({
        queryKey: ["control", deviceType, deviceId, deviceDataHours],
        queryFn: ({ signal }) => getControlData(deviceType, deviceId, deviceDataHours, signal),
        enabled: Boolean(deviceType && deviceId),
        refetchOnWindowFocus: false,
        gcTime: 6000,
        meta: {
            persist: false,
        },
    });
}
export function useEventCount(deviceId: string, enabled: boolean) {
    const innerEnabled = enabled && Boolean(deviceId);

    return useQuery({
        queryKey: ["EventCount", deviceId],
        queryFn: async ({ signal }): Promise<EventInfoResult> => {
            return await getEventInfos(deviceId, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        totalAlarms: 0,
                        totalEvents: 0,
                        newestEventDate: new Date(),
                        oldestEventDate: new Date(),
                        id: "",
                    };
                });
        },
        refetchInterval: dashboardConfig.api.refetchInterval,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        enabled: innerEnabled,
        gcTime: 60_000,
        staleTime: 0,
        meta: {
            persist: false,
        },
    });
}
export function useEvents(
    deviceId: string,
    page: number,
    pageSize: number,
    filter: EventFilter,
    enabled = true
) {
    return useQuery({
        queryKey: [
            "Events",
            deviceId,
            page,
            pageSize,
            filter.eventType?.join(",") ?? "",
            filter.searchText ?? "",
            filter.showOnlyActive,
            filter.showOnlyNotAcknowledged,
            filter.fromDate?.toString(),
            filter.toDate?.toString(),
            filter.deviceUuid ?? "",
            filter.sortBy,
            filter.sortOrder,
        ],
        queryFn: async (): Promise<EventPaginatedResult> => {
            return await getEvents(deviceId, page, pageSize, filter)
        },

        enabled: Boolean(deviceId) && enabled,

        gcTime: 60_000,
    });
}

export function useConfirmEvent() {
    return useMutation({
        mutationKey: ["ConfirmEvent"],
        mutationFn: async (payload: confirmEventPayload): Promise<EventConfirmResult> => {
            return await confirmEvent(payload.deviceId, payload.event);
        },
    });
}

export function useIgnoreEvent() {
    return useMutation({
        mutationKey: ["IgnoreEvent"],
        mutationFn: async (payload: ignoreEventPayload): Promise<void> => {
            return await ignoreEvent(payload.deviceId, payload.event);
        },
    });
}

export function useCases(
    deviceId: string,
    deviceType: string,
    page: number,
    pageSize: number,
    filter: CaseFilter,
    options?: { enabled?: boolean }
) {
    return useQuery({
        queryKey: [
            "Cases",
            deviceId,
            deviceType,
            page,
            pageSize,
            filter.searchText ?? "",
            filter.fromDate?.toString() ?? "",
            filter.toDate?.toString() ?? "",
            filter.sortBy,
            filter.sortOrder,
            filter.personUuids?.join(",") ?? "",
            filter.categoryUuids?.join(",") ?? "",
            filter.componentUuids?.join(",") ?? "",
            filter.deviceUuids?.join(",") ?? "",
            filter.showClosed,
        ],

        queryFn: ({ signal }): Promise<CasePaginatedResult> =>
            getCases(
                deviceId,
                deviceType,
                page,
                pageSize,
                {
                    ...filter,

                    personUuids:
                        filter.personUuids?.length
                            ? filter.personUuids
                            : null,

                    deviceUuids:
                        filter.deviceUuids?.length
                            ? filter.deviceUuids
                            : null,
                },
                signal
            ),
        enabled: options?.enabled ?? true,
        gcTime: 60_000,
    });
}
export function useCase(caseId: number) {
    return useQuery({
        queryKey: ["Case", caseId],
        queryFn: async ({ signal }): Promise<Case> => {
            return await getCase(caseId, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        content: "",
                        deviceUuid: "",
                        reporterUuid: undefined,
                        editorUuid: undefined,
                        editorName: undefined,
                        reporterName: undefined,
                        subject: "",
                        closed: undefined,
                        closedAt: undefined,
                        createdAt: undefined,
                        id: 0,
                        name: "",
                        datasourceId: "",
                        formattedId: "",
                        serialNumber: "",
                        timeStamp: new Date(),
                        startDate: "",
                        endDate: ""
                    };
                });
        },
    });
}

export function useAddNewCase() {
    return useMutation({
        mutationKey: ["AddNewCase"],
        mutationFn: async (payload: casePayload): Promise<CaseResult> => {
            return await addNewCase(payload.case);
        },
    });
}

export function useUpdateCase() {
    return useMutation({
        mutationKey: ["UpdateCase"],
        mutationFn: async (payload: casePayload): Promise<CaseResult> => {
            return await updateCase(payload.case);
        },
    });
}

export function useClosedCase() {
    return useMutation({
        mutationKey: ["ClosedCase"],
        mutationFn: async (payload: casePayload): Promise<CaseResult> => {
            return await closedCase(payload.case);
        },
    });
}

export function useCaseDevicesDefault(enabled: boolean) {
    return useQuery({
        queryKey: ["case-devices-default"],
        queryFn: ({ signal }) => fetch("/api/case/devices", { signal }).then((res) => res.json()),
        enabled,
        staleTime: 60_000,
    });
}
export function useCategories(searchText: string) {
    return useQuery<CategoryListResponse>({
        queryKey: ["categories", searchText],
        queryFn: () =>
            getCategories(
                {
                    searchText,
                    page: 1,
                    pageSize: 20,
                    sortBy: "priority",
                    sortOrder: SortOrder.Ascending
                }),
    });
}
export function useComponents(searchText: string) {
    return useQuery<ComponentListResponse>({
        queryKey: ["components", searchText],
        queryFn: () =>
            getComponents(
                {
                    searchText,
                    page: 1,
                    pageSize: 20,
                    sortBy: "name",
                    sortOrder: SortOrder.Ascending
                }),
    });
}
export function useOperations(
    deviceId: string,
    deviceType: string,
    page: number,
    pageSize: number,
    filter: OperationFilter,
    options?: { enabled?: boolean }

) {
    return useQuery({
        queryKey: [
            "Operations",
            deviceId,
            deviceType,
            page,
            pageSize,
            filter.searchText ?? "",
            filter.fromDate?.toISOString() ?? "",
            filter.toDate?.toISOString() ?? "",
            filter.sortBy,
            filter.sortOrder,
            filter.personUuids?.join(",") ?? "",
            filter.deviceUuids?.join(",") ?? "",
            filter.showClosed,
            filter.isArchivedOnly,
            filter.hasTroubleshootingOnly,
            filter.hasPendingWorkOnly,
            filter.hasEquipmentReplacementOnly,
            filter.requiresElectricalPlanUpdateOnly,
        ],

        queryFn: ({ signal }): Promise<OperationPaginatedResult> => {
            const requestFilter: OperationFilter = {
                ...filter,

                personUuids:
                    filter.personUuids?.length
                        ? filter.personUuids
                        : null,

                deviceUuids:
                    filter.deviceUuids?.length
                        ? filter.deviceUuids
                        : null,
            };

            return getOperations(
                deviceId,
                deviceType,
                page,
                pageSize,
                requestFilter,
                signal
            );
        },
        enabled: options?.enabled ?? true,
        gcTime: 60_000,
    });
}
export function useOperation(operationId: number) {
    return useQuery({
        queryKey: ["Operation", operationId],
        queryFn: async ({ signal }): Promise<Operation> => {
            return await getOperation(operationId, signal)
                .then((x) => x)
                .catch(() => {
                    return {
                        troubleshooting: "",
                        deviceUuid: "",
                        creatorUuid: "",
                        editorUuid: "",
                        editorName: "",
                        creatorName: "",
                        description: "",
                        archivedAt: new Date(),
                        updatedAt: new Date(),
                        createdAt: new Date(),
                        id: 0,
                        capacity: 0,
                        commissioning: new Date(),
                        datasourceId: "",
                        formattedId: "",
                        hasElectricalVehicleCharging: false,
                        hasPendingDescription: false,
                        hasTroubleshooting: false,
                        isArchived: false,
                        isElectricalPlanUpdateNecessary: false,
                        isEquipmentReplaced: false,
                        isTroubleshootingFinished: false,
                        lastOperation: new Date(),
                        name: "",
                        pendingDescription: "",
                        replacedEquipments: "",
                        serialnumber: "",
                        caseIds: []
                    };
                });
        },
    });
}

export function useAddNewOperation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["AddNewOperation"],
        mutationFn: async (payload: operationPayload): Promise<OperationResult> => {
            return await addNewOperation(payload.operation);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === "Operations",
            });
        },
    });
}

export function useUpdateOperation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["UpdateOperation"],
        mutationFn: async (payload: operationPayload): Promise<OperationResult> => {
            return await updateOperation(payload.operation);
        },
        onSuccess: async (_result, variables) => {
            const id = variables.operation.id; // <- anpassen falls bei dir anders

            await queryClient.invalidateQueries({ queryKey: ["Operation", id] });
            await queryClient.refetchQueries({ queryKey: ["Operation", id], exact: true }); // <- erzwingt Backend call

            // optional: auch Listen invalidieren, falls du welche hast:
            await queryClient.invalidateQueries({ queryKey: ["Operations"] });
        },
    });
}

/*Monthly Reports*/

export function useMonthReport(filter?: MonthlyReportFilter) {
    return useQuery({
        queryKey: ["monthReport", JSON.stringify(filter ?? {})],
        queryFn: ({ signal }) => {
            if (!filter) throw new Error("Missing filter");
            return fetchMonthReport(filter!, signal);
        },
        enabled: !!filter,
        gcTime: 60_000,
    });
}

const OVERVIEW_SNAPSHOT_MIN_WRITE_INTERVAL = 60_000;

const overviewLastWriteAt: Record<string, number> = {};
const overviewLastJson: Record<string, string> = {};

const getOverviewStorageKey = (partialLoad: boolean) => `SCADA_OVERVIEW_${partialLoad ? "PARTIAL" : "FULL"}`;
export function readOverviewSnapshot(partialLoad: boolean): Overview | undefined {
    try {
        const raw = localStorage.getItem(getOverviewStorageKey(partialLoad));
        return raw ? (JSON.parse(raw) as Overview) : undefined;
    } catch {
        return undefined;
    }
}

function writeOverviewSnapshotThrottled(partialLoad: boolean, data: Overview) {
    try {
        const key = getOverviewStorageKey(partialLoad);
        const now = Date.now();

        if (
            overviewLastWriteAt[key] &&
            now - overviewLastWriteAt[key] < OVERVIEW_SNAPSHOT_MIN_WRITE_INTERVAL
        ) {
            return;
        }

        const json = JSON.stringify(data);

        if (overviewLastJson[key] === json) {
            overviewLastWriteAt[key] = now;
            return;
        }

        localStorage.setItem(key, json);

        overviewLastJson[key] = json;
        overviewLastWriteAt[key] = now;
    } catch {
        // localStorage nicht verfügbar oder voll
    }
}






